import React, { useRef, useState } from "react";
import aiTextGenerate from "assets/images/ai_text_generator.svg";
import magicStick from "assets/images/magicStick.svg";
import selectDocument from "assets/images/select_document.svg";
import UploadFile from "assets/images/upload_file.svg";
import { getSelectedLanguage } from "utils/language";
import { useTranslation } from "react-i18next";
import { reportService } from "../../services/reportService";

const AiGenerateReportField = ({ onCreated }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState("");
    const textareaRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [etaHint, setEtaHint] = useState("");
    const [etaModal, setEtaModal] = useState("");

    const handleInput = (e) => {
        const textarea = textareaRef.current;
        textarea.style.height = "42px";
        textarea.style.height = textarea.scrollHeight + "px";
        setValue(e.target.value);
    };

    const handleFiles = (files) => {
        const fileArray = Array.from(files);
        const totalFiles = [...uploadedFiles, ...fileArray].slice(0, 5); // max 5 files
        setUploadedFiles(totalFiles);
        setShowModal(false);
    };

    const handleFileChange = (e) => handleFiles(e.target.files);

    const handleDrop = (e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    };

    const getFileTypeIcon = (file) => {
        if (!file) return null;
        const type = file.type;
        if (type.includes("pdf")) return "📄";
        if (type.includes("image")) return "🖼️";
        if (type.includes("word")) return "📝";
        return "📁";
    };

    const handleSubmit = async () => {
        setError("");
        setSuccess("");
        if (!value.trim()) {
            setError(t("reports.aiGenerate.promptRequired"));
            return;
        }

        setSubmitting(true);
        setEtaHint(t("reports.aiGenerate.etaHint"));
        try {
            const created = await reportService.createCustomReport({
                prompt: value,
                attachments: uploadedFiles,
                language: getSelectedLanguage(),
            });
            if (typeof onCreated === "function" && created) {
                onCreated(created);
            }
            const backendEta = created?.metadata_json?.eta_hint || "2-5 minutes";
            setSuccess(t("reports.aiGenerate.requestSent", { eta: backendEta }));
            setEtaModal(t("reports.aiGenerate.requestSent", { eta: backendEta }));
            setValue("");
            setUploadedFiles([]);
            if (textareaRef.current) {
                textareaRef.current.style.height = "42px";
            }
        } catch (err) {
            setError(err?.message || t("reports.aiGenerate.failed"));
            setEtaHint("");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="generate_report_wrap position-relative">
            {etaModal && (
                <div className="report-eta-modal" onClick={() => setEtaModal("")}>
                    <div className="report-eta-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="fw-semibold mb-2">{t("reports.generating")}</div>
                        <div className="small">{etaModal}</div>
                        <button
                            type="button"
                            className="btn btn-outline-light btn-sm mt-3"
                            onClick={() => setEtaModal("")}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
            <div className="d-flex gap-4 align-items-start generate_report_box ">
                <div className="generate_report_field position-relative">
                    {/* Uploaded files inside field */}
                    <div className="inner-files-wrapper">
                        {uploadedFiles.map((file, index) => (
                            <div className="inner-file-preview" key={index}>
                                <span className="file-icon">{getFileTypeIcon(file)}</span>
                                <button
                                    className="remove-file-btn"
                                    onClick={() => removeFile(index)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>

                    <textarea
                        ref={textareaRef}
                        className={`search-txt ${uploadedFiles.length ? "with-files" : ""}`}
                        placeholder={t("reports.aiGenerate.placeholder")}
                        value={value}
                        onInput={handleInput}
                        rows={1}
                    />

                    <div className="magic_icon" role="button" tabIndex={0}>
                        <img src={magicStick} alt={t("reports.aiGenerate.magicAlt")} className="icon" />
                    </div>

                    <div
                        className="selectDocument"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowModal(true)}
                    >
                        <img src={selectDocument} alt={t("reports.aiGenerate.selectDocumentAlt")} className="icon" />
                    </div>
                </div>

                <button
                    className="ai-assistant-btn d-flex align-items-center gap-2 px-4"
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    <img src={aiTextGenerate} alt={t("reports.aiGenerate.assistantAlt")} />
                    <span>{submitting ? t("reports.generating") : t("reports.aiGenerate.generate")}</span>
                </button>
            </div>

            {(error || success) && (
                <div className="mt-2">
                    {error && <div className="text-danger small">{error}</div>}
                    {success && <div className="text-success small">{success}</div>}
                </div>
            )}
            {!error && !success && etaHint && (
                <div className="mt-2 text-light small">{etaHint}</div>
            )}

            {/* Upload Modal */}
            {showModal && (
                <div className="upload-overlay" onClick={() => setShowModal(false)}>
                    <div
                        className="upload-modal"
                        onClick={(e) => e.stopPropagation()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        <div className="upload-content text-center">
                            <p className="mb-0">
                                <span className="upload-icon">
                                    <img src={UploadFile} alt="" />
                                </span>
                                <span className="text_purple"> {t("reports.aiGenerate.uploadFile")} </span> {t("reports.aiGenerate.uploadHint")}
                            </p>
                            <input
                                type="file"
                                id="fileInput"
                                multiple
                                onChange={handleFileChange}
                                className="file-input"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AiGenerateReportField;
